/**
 * Capture Sharpening — Richardson-Lucy deconvolution.
 *
 * Compensates for the camera's optical low-pass (anti-aliasing) filter
 * blur. Operates in scene-linear Float32 RGB (currently linear Rec.709
 * inside the RAW decoder, but the math is colour-space-agnostic — any
 * linear non-negative-or-near-zero RGB works). Mirror-extend boundary
 * handling; clamped to a 4× input upper bound to prevent runaway noise
 * amplification.
 *
 * References (clean-room from public maths — no GPLv3 source consulted):
 *   • Richardson, W.H. (1972), "Bayesian-Based Iterative Method of Image
 *     Restoration", JOSA Vol. 62, Issue 1, pp. 55-59.
 *   • Lucy, L.B. (1974), "An iterative technique for the rectification
 *     of observed distributions", Astronomical Journal, Vol. 79, p. 745.
 *
 * Algorithm (per channel, given observed `d`, blur kernel `psf`):
 *   f_0 = d
 *   for i in 0..N:
 *       est = f_i ⊛ psf
 *       err = d / max(est, ε)
 *       cor = err ⊛ psf_flipped   (== err ⊛ psf when psf symmetric)
 *       f_{i+1} = f_i × cor
 *
 * Memory model (Codex P8 STOP — buffer pooling):
 *   For a w·h image with N iterations in 'rgb' mode, the algorithm
 *   needs five planar Float32 scratches per channel (≤ 5 × 4 = 20 bytes
 *   per pixel transient overhead, regardless of N). 'luminance' mode
 *   re-uses the same five scratches once. Buffers are allocated ONCE
 *   per call and re-used across iterations:
 *     • `f`     — current estimate (planar)
 *     • `dPos`  — clamped observation (planar; constant across iters)
 *     • `tmp`   — H-pass output (planar; reused for every blur)
 *     • `workA` — full blur output (used for `est`, then for `cor`)
 *     • `workB` — ratio (= d / est) buffer
 *   On a 24 MP RAW that's ~480 MB transient — well below pre-fix 768
 *   MB. After the call returns, all five scratches are GC-eligible.
 *
 * @module src/pipeline/capture-sharpen
 */

const EPS_DIVISION = 1e-6;          /* prevent /0 in ratio update */
const MAX_AMPLIFICATION = 4.0;       /* upper bound vs. original pixel */

/**
 * Build a symmetric, unit-sum 1D Gaussian kernel.
 *
 * @param {number} sigma   standard deviation in pixels (>0, finite)
 * @param {number} [size]  half-width; default ceil(3·σ) for >99.7% energy.
 *                         Must be a non-negative integer if provided.
 * @returns {Float32Array}  length 2·size+1
 */
export function gaussianKernel1D(sigma, size) {
    if (!Number.isFinite(sigma) || sigma <= 0) {
        throw new Error(`gaussianKernel1D: sigma must be a finite positive number (got ${sigma})`);
    }
    let r;
    if (size == null) {
        r = Math.max(1, Math.ceil(3 * sigma));
    } else {
        if (!Number.isFinite(size) || size < 0) {
            throw new Error(`gaussianKernel1D: size must be a non-negative finite number (got ${size})`);
        }
        r = size | 0;
        if (r < 1) r = 1;       /* degenerate kernel of length 1 is a no-op; bump to ≥3-tap */
    }
    const len = 2 * r + 1;
    const k = new Float32Array(len);
    const inv2s2 = 1 / (2 * sigma * sigma);
    let sum = 0;
    for (let i = -r; i <= r; i++) {
        const v = Math.exp(-i * i * inv2s2);
        k[i + r] = v;
        sum += v;
    }
    /* Normalise so the kernel has unit DC response — exactly preserves
     * a flat input under the convolution. */
    const inv = 1 / sum;
    for (let i = 0; i < len; i++) k[i] *= inv;
    return k;
}

/**
 * Reflect an out-of-bounds index back into [0, n-1] using mirror-extend
 * (no boundary repetition — the edge sample is reflected, not doubled).
 *
 * Examples (n=5): -1→1, -2→2, 5→3, 6→2; indices beyond ±n handled by
 * a single modulo into the period 2(n-1).
 */
function mirror(i, n) {
    if (n <= 1) return 0;
    const period = 2 * (n - 1);
    let m = i % period;
    if (m < 0) m += period;
    return m < n ? m : period - m;
}

/**
 * Separable 1D Gaussian convolution on a single-channel buffer.
 *
 * Reads from `src`, writes to `dst`. `axis = 0` runs the kernel
 * horizontally (across X); `axis = 1` runs vertically (across Y).
 * `dst` must be distinct from `src`.
 *
 * @param {Float32Array} src     input  (width × height)
 * @param {Float32Array} dst     output (width × height; distinct from src)
 * @param {number} width
 * @param {number} height
 * @param {Float32Array} kernel  unit-sum, symmetric, length 2r+1
 * @param {0|1} axis
 */
function convolve1DPlanar(src, dst, width, height, kernel, axis) {
    const r = (kernel.length - 1) >> 1;
    if (axis === 0) {
        for (let y = 0; y < height; y++) {
            const row = y * width;
            for (let x = 0; x < width; x++) {
                let acc = 0;
                for (let k = -r; k <= r; k++) {
                    const xx = mirror(x + k, width);
                    acc += src[row + xx] * kernel[k + r];
                }
                dst[row + x] = acc;
            }
        }
    } else {
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                let acc = 0;
                for (let k = -r; k <= r; k++) {
                    const yy = mirror(y + k, height);
                    acc += src[yy * width + x] * kernel[k + r];
                }
                dst[y * width + x] = acc;
            }
        }
    }
}

/**
 * Public separable 1D Gaussian on RGBA Float32 in-place.
 *
 * Test surface: lets the test gate verify the building block in
 * isolation before exercising the full RL iteration. Allocates two
 * planar scratch arrays for the planar→pack round-trip; not on a hot
 * path, so the per-call alloc is acceptable.
 *
 * @param {Float32Array} rgba    RGBA Float32 buffer (length = 4·w·h)
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} kernel
 * @param {0|1} axis             0 = horizontal, 1 = vertical
 */
export function applyGaussian1D(rgba, w, h, kernel, axis) {
    if (!(rgba instanceof Float32Array)) {
        throw new Error('applyGaussian1D: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error(`applyGaussian1D: length ${rgba.length} != 4·${w}·${h}`);
    }
    const ax = axis | 0;
    if (ax !== 0 && ax !== 1) {
        throw new Error(`applyGaussian1D: axis must be 0 or 1 (got ${axis})`);
    }
    const n = w * h;
    const planeIn  = new Float32Array(n);
    const planeOut = new Float32Array(n);
    for (let c = 0; c < 3; c++) {
        for (let i = 0, j = c; i < n; i++, j += 4) planeIn[i] = rgba[j];
        convolve1DPlanar(planeIn, planeOut, w, h, kernel, ax);
        for (let i = 0, j = c; i < n; i++, j += 4) rgba[j] = planeOut[i];
    }
}

/**
 * Full 2D Gaussian blur with separable H-then-V passes — into-style.
 * Caller provides `tmp` (single-channel scratch) and `dst` distinct
 * from `src`. No allocation inside the loop.
 *
 * @param {Float32Array} src     input planar (w·h)
 * @param {Float32Array} dst     output planar (w·h, distinct from src and tmp)
 * @param {Float32Array} tmp     H-pass scratch (w·h, distinct from src and dst)
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} kernel
 */
function blurPlanarInto(src, dst, tmp, w, h, kernel) {
    convolve1DPlanar(src, tmp, w, h, kernel, 0);
    convolve1DPlanar(tmp, dst, w, h, kernel, 1);
}

/* Rec.709 luminance weights — applied directly to scene-linear RGB.
 * For ACEScg these are slightly off-axis (different primaries), but the
 * result is still a valid achromatic projection — the chromaticity is
 * preserved by reconstructing R/G/B as `orig × (Y'/Y)`, where Y is the
 * dot-product with the same weights both ways. */
const LUMA_R = 0.2126, LUMA_G = 0.7152, LUMA_B = 0.0722;

/**
 * Run N Richardson-Lucy iterations on a single planar channel using
 * pre-allocated scratch buffers. `f` and `dPos` are mutated; the final
 * estimate ends up in `f`.
 *
 * @param {Float32Array} f       current estimate (in/out, planar)
 * @param {Float32Array} dPos    clamped observation (read-only; planar)
 * @param {Float32Array} tmp     H-pass scratch
 * @param {Float32Array} est     forward-blur result (overwritten)
 * @param {Float32Array} ratio   d / est (overwritten)
 * @param {Float32Array} correction backward correlation result (overwritten;
 *                                  may alias `est` for a 4-buffer plan)
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} kernel
 * @param {number} iterations
 */
function richardsonLucyInto(
    f, dPos, tmp, est, ratio, correction, w, h, kernel, iterations,
) {
    const n = w * h;
    for (let it = 0; it < iterations; it++) {
        /* est = f ⊛ psf */
        blurPlanarInto(f, est, tmp, w, h, kernel);
        /* ratio = dPos / max(est, ε) */
        for (let i = 0; i < n; i++) {
            const e = est[i] < EPS_DIVISION ? EPS_DIVISION : est[i];
            ratio[i] = dPos[i] / e;
        }
        /* correction = ratio ⊛ psf  (psf symmetric → correlation == convolution) */
        blurPlanarInto(ratio, correction, tmp, w, h, kernel);
        /* f *= correction; clamp to [0, MAX_AMPLIFICATION × dPos] */
        for (let i = 0; i < n; i++) {
            let v = f[i] * correction[i];
            const cap = dPos[i] * MAX_AMPLIFICATION;
            if (v < 0) v = 0;
            else if (cap > 0 && v > cap) v = cap;
            f[i] = v;
        }
    }
}

/**
 * Capture sharpening on Float32 RGBA — Richardson-Lucy with Gaussian PSF.
 *
 * Operates in-place on the input `rgba`. Alpha is preserved untouched.
 * Allocates five planar Float32 scratches once per call (regardless of
 * iteration count): {f, dPos, tmp, est, ratio}. The 'correction' buffer
 * aliases `est` (legal — `est` is consumed before `correction` is
 * written). 'rgb' mode walks the loop three times; 'luminance' once.
 *
 * @param {Float32Array} rgba          scene-linear RGBA Float32 (length 4·w·h)
 * @param {number} w
 * @param {number} h
 * @param {object} [opts]
 * @param {number} [opts.sigma=0.7]    Gaussian σ in pixels (camera AA-filter blur)
 * @param {number} [opts.iterations=3] number of RL iterations
 * @param {'luminance'|'rgb'} [opts.mode='luminance']  channel scope
 * @param {number} [opts.strength=1.0] mix in [0, 1] between observed and sharpened
 * @returns {Float32Array}             same `rgba` reference
 */
export function captureSharpen(rgba, w, h, opts) {
    if (!(rgba instanceof Float32Array)) {
        throw new Error('captureSharpen: rgba must be Float32Array');
    }
    const expected = w * h * 4;
    if (rgba.length !== expected) {
        throw new Error(`captureSharpen: length ${rgba.length} != 4·${w}·${h} (${expected})`);
    }
    if (w <= 0 || h <= 0) {
        return rgba;        /* trivial empty buffer */
    }

    const o = opts || {};
    const sigma      = (o.sigma      != null) ? +o.sigma      : 0.7;
    const iterations = (o.iterations != null) ? (o.iterations | 0) : 3;
    const mode       = (o.mode === 'rgb') ? 'rgb' : 'luminance';
    const strength   = (o.strength   != null) ? Math.max(0, Math.min(1, +o.strength)) : 1.0;

    if (!Number.isFinite(sigma) || sigma <= 0) {
        throw new Error(`captureSharpen: sigma must be a finite positive number (got ${sigma})`);
    }
    if (iterations < 1 || strength === 0) {
        return rgba;        /* no-op short-circuits */
    }

    const kernel = gaussianKernel1D(sigma);
    const n = w * h;

    /* Allocate the five-plane scratch pool ONCE per call. Reused across
     * iterations AND (in 'rgb' mode) across the three colour channels.
     * `correction` aliases `est` — `est` is fully consumed by the ratio
     * step before `correction` is computed, so the alias is safe. */
    const f          = new Float32Array(n);
    const dPos       = new Float32Array(n);
    const tmp        = new Float32Array(n);
    const est        = new Float32Array(n);
    const ratio      = new Float32Array(n);
    const correction = est;       /* alias — saves a w·h Float32Array */

    if (mode === 'rgb') {
        /* Per-channel RL — better detail preservation, more chroma noise.
         * For each channel: load planar → run RL → write back into rgba.
         * The original RGB is still sitting in `rgba` (RL writes into the
         * planar `f` scratch, not back into `rgba`), so the strength
         * mix can read directly from rgba[j] without a separate copy. */
        const u = 1 - strength, t = strength;
        for (let c = 0; c < 3; c++) {
            /* Initialise f and dPos = clamp(input, ε). */
            for (let i = 0, j = c; i < n; i++, j += 4) {
                const v = rgba[j];
                const cl = v < EPS_DIVISION ? EPS_DIVISION : v;
                f[i]    = cl;
                dPos[i] = cl;
            }
            richardsonLucyInto(f, dPos, tmp, est, ratio, correction,
                w, h, kernel, iterations);
            /* Write back: rgba[c] = mix(orig, f, strength). The original
             * is still at rgba[j] — RL never wrote to `rgba`. */
            if (strength >= 1) {
                for (let i = 0, j = c; i < n; i++, j += 4) rgba[j] = f[i];
            } else {
                for (let i = 0, j = c; i < n; i++, j += 4) {
                    rgba[j] = u * rgba[j] + t * f[i];
                }
            }
        }
        return rgba;
    }

    /* Luminance mode: extract Y, sharpen, re-apply Y'/Y ratio per
     * channel. This preserves chromaticity — only the brightness is
     * deconvolved. Caveat: very dark pixels have a stable ratio
     * (numerator → 0 in lockstep with denominator) but we still floor
     * at ε to avoid propagating noise.
     *
     * Optimisation: `dPos` doubles as the original-luminance store
     * (it's the clamped Y, constant across iterations). The
     * post-iteration mix uses `dPos[i]` rather than allocating a
     * separate `Y` buffer. */
    for (let i = 0, j = 0; i < n; i++, j += 4) {
        const y = LUMA_R * rgba[j] + LUMA_G * rgba[j + 1] + LUMA_B * rgba[j + 2];
        const cl = y < EPS_DIVISION ? EPS_DIVISION : y;
        f[i]    = cl;
        dPos[i] = cl;
    }
    richardsonLucyInto(f, dPos, tmp, est, ratio, correction,
        w, h, kernel, iterations);
    /* `dPos` is the clamped original-Y; `f` is the sharpened estimate.
     * Mix via strength, then propagate `Y'/Y` per channel. */
    const tL = strength, uL = 1 - strength;
    for (let i = 0, j = 0; i < n; i++, j += 4) {
        const yo   = dPos[i];
        const yMix = uL * yo + tL * f[i];
        const r    = yMix / yo;
        const a0 = rgba[j]     * r;
        const a1 = rgba[j + 1] * r;
        const a2 = rgba[j + 2] * r;
        rgba[j]     = a0 < 0 ? 0 : a0;
        rgba[j + 1] = a1 < 0 ? 0 : a1;
        rgba[j + 2] = a2 < 0 ? 0 : a2;
    }
    return rgba;
}
